import _ from 'lodash';
import API from '@scale/frontend-lib/dashboard-api';
import {
  CandidateMessageWithMetadata,
  ContextType,
  InteractionStepType,
  InteractionStepResponse,
  MultiTurnInteractionConfiguration,
  ThreadedChatResponseWithMaybeUrl,
  ThreadedChatResponse,
  WorkerCommentEntry,
  AddWorkerCommentRequest,
  AddFeedbackToWorkerCommentRequest,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared/defaults';
import { ChatHistory, isStepVisible } from '@scale/shared/chat/api';
import { RESPONSE_GENERATOR_STEPS } from '@scale/shared/chat/steps';
import apiFetch from '@scale/frontend-lib/api-fetch';
import retry from '@scale/shared/utils/retry';

/* -----------------------------------------------------------------------------
 *  Functions related to views
 * ----------------------------------------------------------------------------- */
export function shouldUseReviewHeader(reviewLevel?: ReviewLevel, isReviewMode?: boolean) {
  return (
    isReviewMode &&
    !_.isUndefined(reviewLevel) &&
    ![ReviewLevel.Attempt, ReviewLevel.ReviewConsensus].includes(reviewLevel)
  );
}
/* -----------------------------------------------------------------------------
 *  Functions related to loading a chat response
 * ----------------------------------------------------------------------------- */
export async function loadResponseWithMaybeUrl(
  response: ThreadedChatResponseWithMaybeUrl,
  projectId: string | undefined,
): Promise<ThreadedChatResponse> {
  return await retry(
    async () => {
      const { response: loadedResponse } = await apiFetch(null, `internal/genai/loadChatResponse`, {
        method: 'POST',
        body: {
          response,
          projectId,
        },
      });

      return loadedResponse;
    },
    {
      limit: 3,
    },
  );
}

/* -----------------------------------------------------------------------------
 *  Functions related to filtering responses upon SBQ
 * ----------------------------------------------------------------------------- *
/*
 * This function is used to clean responses once they are SBQ'd from an upper review layer.
 * For steps that are not visible in the current review level, we replace their existing response with a skipped response.
 * This mimics how the responses would look like if they were to be submitted fresh from this review level again.
 * TODO: Move this functionality into the pipeline when tasks are SBQ'd or routed to different review levels.
 */
// eslint-disable-next-line complexity
export function updateResponseForReviewLevel({
  response,
  config,
  reviewLevel,
  readOnly,
}: {
  response: ThreadedChatResponse;
  config: MultiTurnInteractionConfiguration;
  reviewLevel: ReviewLevel | undefined;
  readOnly?: boolean;
}) {
  if (_.isNil(reviewLevel)) {
    return response;
  }

  const chatHistory = new ChatHistory(config, response);
  const stepsWithResponses = chatHistory.getFlattenedStepsWithResponses();
  const attemptResponses = _.cloneDeep(response.responses);
  let lastMessage = '';

  for (let i = 0; i < stepsWithResponses.length; i++) {
    const [stepParams, stepResponse] = stepsWithResponses[i];

    if (!stepParams || !stepResponse) {
      throw new Error(
        'step params or response is undefined when filtering responses for the current review level',
      );
    }

    /*
     * Replace the existing response with a skipped response if both conditions are met
     * 1. The step is not visible in the current review level
     * 2. The current review level is less than every review level in visibility_restricted
     * The second condition is necessary because responses should only be skipped if they have
     * never reached a review level in which they are visible.
     */
    const visible_review_levels: ReviewLevel[] = stepParams.params.visibility_restricted ?? [];
    if (
      !isStepVisible(stepParams, reviewLevel) &&
      visible_review_levels.every(level => reviewLevel < level) &&
      !config.turnLocking
    ) {
      let context = chatHistory.getNullContext(stepParams);

      // Maintain the candidates context for selector steps with force consistent responses on
      const keepCandidatesContext =
        stepParams.type === InteractionStepType.ModelResponseSelector &&
        stepParams.params.force_consistent_responses;

      if (keepCandidatesContext) {
        // Filter out existing from candidates since it may be updated in the current attempt
        const candidates = (
          stepResponse.context.candidates as CandidateMessageWithMetadata[]
        ).filter(candidate => candidate.id !== 'existing');
        context = { ...context, candidates };
      }

      const skippedResponse = {
        type: stepParams.type,
        context,
        contextType: ContextType.Skipped,
        output: lastMessage,
        // forcing the cast to InteractionStepResponse to avoid type errors
        // because getFlattenedStepsWithResponses is using a tuple of StepParamsForTypeWithStage<InteractionStepType> and IndexedInteractionStepResponse, and there's a hard limit on the number of elements in a tuple
      } as unknown as InteractionStepResponse;

      attemptResponses[i] = { ...attemptResponses[i], ...skippedResponse };
    }
    // Only update lastMessage if the step is visible
    else {
      // Update the message for MultiTurnContinue steps
      if (stepParams.type === InteractionStepType.MultiTurnContinue) {
        attemptResponses[i].context = {
          ...attemptResponses[i].context,
          message: lastMessage,
        };
      }

      // Clear Quality Measurement steps as long as we are not in read-only mode
      if (!readOnly && stepParams.type === InteractionStepType.QualityMeasurement) {
        const skippedResponse: InteractionStepResponse = {
          type: stepParams.type,
          context: chatHistory.getNullContext(stepParams),
          contextType: ContextType.Skipped,
          output: lastMessage,
        };
        attemptResponses[i] = { ...attemptResponses[i], ...skippedResponse };
      }

      /*
       * Update the lastMessage in three cases
       * 1. For before steps, we only consider the output from prompt input steps
       * 2. For non-before steps, we only consider the output from response generator steps
       * 3. lastMessage should be reset once we reach the end of the before steps or a MultiTurnContinue step
       * This should match our usage of getMostRecentPrompt and getMostRecentResponse
       */
      if (i < config.before.length && stepParams.type === InteractionStepType.PromptInput) {
        lastMessage = stepResponse.output;
      } else if (RESPONSE_GENERATOR_STEPS.includes(stepParams.type)) {
        lastMessage = stepResponse.output;
      }

      if (
        i === config.before.length - 1 ||
        stepParams.type === InteractionStepType.MultiTurnContinue
      ) {
        lastMessage = '';
      }
    }
  }

  return { ...response, responses: attemptResponses };
}

/* -----------------------------------------------------------------------------
 *  Functions related to worker comment feedback
 * ----------------------------------------------------------------------------- */
export async function submitReviewerWorkerComments(
  workerComments: WorkerCommentEntry[],
  assignmentId: string,
  author: string,
) {
  const requests: AddWorkerCommentRequest[] = workerComments.map(workerComment => {
    const {
      comment,
      childId,
      locationStart,
      locationEnd,
      overrideChildId,
      turn,
      title,
      quotedText,
    } = workerComment;
    return {
      assignmentId,
      comment,
      author,
      childId: overrideChildId ?? childId,
      locationStart,
      locationEnd,
      turn,
      title,
      quotedText,
    };
  });

  if (!requests.length) {
    return;
  }

  await API.genai.bulkAddWorkerComments({ requests });
}

export async function submitAttempterFeedbackForWorkerComments(
  workerComments: WorkerCommentEntry[],
  workerId: string,
) {
  const requests: AddFeedbackToWorkerCommentRequest[] = workerComments
    .filter(workerComment => !!workerComment.feedbackResponse)
    .map(workerComment => {
      return {
        workerCommentId: workerComment.workerCommentId ?? '',
        workerId,
        feedbackResponse: workerComment.feedbackResponse,
      };
    });

  if (!requests.length) {
    return;
  }

  await API.genai.bulkAddFeedbackToWorkerComments({ requests });
}

interface MultiReplicaResponseOutput {
  taskIds: string[];
  responses: (ThreadedChatResponse | undefined)[];
  attemptVersions: number[];
  attemptedAtReviewLevels: ReviewLevel[];
  nodeIds: string[];
  locked?: boolean;
}

export async function getReplicaTasks(
  nodeId: string,
  workerId: string,
): Promise<MultiReplicaResponseOutput> {
  const { taskIds, responses, attemptVersions, attemptedAtReviewLevels, nodeIds, locked } =
    await apiFetch(null, `internal/genai/getReplicaTasks`, {
      method: 'POST',
      body: {
        nodeId,
        workerId,
      },
    });
  return {
    taskIds,
    responses,
    attemptVersions,
    attemptedAtReviewLevels,
    nodeIds,
    locked,
  };
}
